import {
  Box,
  Divider,
  Grid,
  Link,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CopyableTypography from "components/Typography/CopyableTypography";
import { useState } from "react";

import { useClosedCohortDetails } from "../useClosedCohortDetails";
import { CohortStateFields } from "./CohortStateFields";
import { CopyableListDisplay } from "./CopyableListDisplay";
import { CopyableListModal } from "./CopyableListModal";
import { StateSummary } from "./StateSummary";
import { SummaryMetric } from "./SummaryMetric";
import { ContactInfo, UniqueContactsModal } from "./UniqueContactsModal";

interface ContactFieldProps {
  label: string;
  value: string;
}

function ContactField({ label, value }: ContactFieldProps) {
  const theme = useTheme();

  return (
    <Stack direction="row" spacing={2} alignItems="baseline">
      <Typography
        variant="caption"
        sx={{
          minWidth: "60px",
          color: theme.palette.text.secondary,
          fontSize: "12px",
          lineHeight: "12px",
        }}
      >
        {label}
      </Typography>
      <CopyableTypography text={value || "--"} sx={{ fontSize: "14px", lineHeight: "1.5" }} />
    </Stack>
  );
}

function ContactCard({ contact }: { contact: ContactInfo }) {
  return (
    <Stack
      direction="column"
      sx={{
        mb: 2,
      }}
    >
      <Stack direction="column" spacing={0.75}>
        <ContactField label="Name" value={contact.name} />
        {contact.title && <ContactField label="Title" value={contact.title} />}
        {contact.email && <ContactField label="Email" value={contact.email} />}
        {contact.phone && <ContactField label="Phone" value={contact.phone} />}
        {contact.address && <ContactField label="Address" value={contact.address} />}
      </Stack>
    </Stack>
  );
}

// How many items to show inline before "View all" link
const MAX_INLINE_NAICS = 3;
const MAX_INLINE_DB_NUMBERS = 3;

export function CohortClosedSummary() {
  const theme = useTheme();
  const { data } = useClosedCohortDetails();

  // Modal state
  const [verifiersModalOpen, setVerifiersModalOpen] = useState(false);
  const [ownerOperatorsModalOpen, setOwnerOperatorsModalOpen] = useState(false);
  const [naicsModalOpen, setNaicsModalOpen] = useState(false);
  const [dbNumbersModalOpen, setDbNumbersModalOpen] = useState(false);

  const summaryData = data?.cohort?.closedCohortSummary;

  if (!summaryData) {
    return <CohortSummarySkeleton />;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Cohort Summary
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <SummaryMetric label="Total Facilities" value={summaryData.readyCount ?? 0} />
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <SummaryMetric label="Total Chemicals" value={data?.cohort?.chemicalCounts?.total ?? 0} />
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <SummaryMetric label="EHS Chemicals" value={data?.cohort?.chemicalCounts?.ehs ?? 0} />
        </Grid>

        <Grid size={{ xs: 12, sm: 8, md: 3 }}>
          <CopyableListDisplay
            label="NAICS Codes"
            items={summaryData.naicsCodes}
            maxItemsShownInline={MAX_INLINE_NAICS}
            viewAllClick={() => setNaicsModalOpen(true)}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 8, md: 3 }}>
          <CopyableListDisplay
            label="D&B Numbers"
            items={summaryData.dbNumbers}
            maxItemsShownInline={MAX_INLINE_DB_NUMBERS}
            viewAllClick={() => setDbNumbersModalOpen(true)}
          />
        </Grid>
      </Grid>

      {/* Verifiers and Owner/Operators Section */}
      {(summaryData.uniqueVerifiers.length > 0 || summaryData.uniqueOwnerOperators.length > 0) && (
        <>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={3}>
            {/* Verifiers */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                Unique Verifiers ({summaryData.uniqueVerifiers.length})
              </Typography>
              {summaryData.uniqueVerifiers.length > 0 ? (
                <Box>
                  <ContactCard contact={summaryData.uniqueVerifiers[0]} />
                  {summaryData.uniqueVerifiers.length > 1 && (
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setVerifiersModalOpen(true)}
                      sx={{ cursor: "pointer" }}
                    >
                      View all {summaryData.uniqueVerifiers.length} verifiers
                    </Link>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  None
                </Typography>
              )}
            </Grid>

            {/* Owner/Operators */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                Owner/Operators ({summaryData.uniqueOwnerOperators.length})
              </Typography>
              {summaryData.uniqueOwnerOperators.length > 0 ? (
                <Box>
                  <ContactCard contact={summaryData.uniqueOwnerOperators[0]} />
                  {summaryData.uniqueOwnerOperators.length > 1 && (
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setOwnerOperatorsModalOpen(true)}
                      sx={{ cursor: "pointer" }}
                    >
                      View all {summaryData.uniqueOwnerOperators.length} owner/operators
                    </Link>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  None
                </Typography>
              )}
            </Grid>
          </Grid>
        </>
      )}

      {/* State-specific summary section */}
      <StateSummary cohortSummaryData={summaryData} />

      <CohortStateFields stateFields={summaryData.cohortStateFieldSummary ?? []} />

      {/* Modals */}
      <UniqueContactsModal
        open={verifiersModalOpen}
        onClose={() => setVerifiersModalOpen(false)}
        title={`Unique Verifiers (${summaryData.uniqueVerifiers.length})`}
        contacts={summaryData.uniqueVerifiers}
      />
      <UniqueContactsModal
        open={ownerOperatorsModalOpen}
        onClose={() => setOwnerOperatorsModalOpen(false)}
        title={`Owner/Operators (${summaryData.uniqueOwnerOperators.length})`}
        contacts={summaryData.uniqueOwnerOperators}
      />
      <CopyableListModal
        open={naicsModalOpen}
        onClose={() => setNaicsModalOpen(false)}
        items={summaryData.naicsCodes}
        title="NAICS Codes"
        noneFoundText="No NAICS codes found"
      />
      <CopyableListModal
        open={dbNumbersModalOpen}
        onClose={() => setDbNumbersModalOpen(false)}
        items={summaryData.dbNumbers}
        title="D&B Numbers"
        noneFoundText="No D&B numbers found"
      />
    </Paper>
  );
}

const CohortSummarySkeleton = () => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Cohort Summary
      </Typography>

      <Grid container spacing={3}>
        {/* Metrics skeletons */}
        {[1, 2, 3].map((i) => (
          <Grid key={i} size={{ xs: 6, sm: 4, md: 2 }}>
            <Skeleton variant="text" width="80%" height={20} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="60%" height={36} />
          </Grid>
        ))}

        {/* NAICS Codes skeleton */}
        <Grid size={{ xs: 12, sm: 8, md: 4 }}>
          <Skeleton variant="text" width="100px" height={20} sx={{ mb: 0.5 }} />
          <Stack direction="row" spacing={1}>
            <Skeleton variant="rounded" width={70} height={28} />
            <Skeleton variant="rounded" width={70} height={28} />
            <Skeleton variant="rounded" width={70} height={28} />
          </Stack>
        </Grid>
      </Grid>

      {/* Verifiers and Owner/Operators Section */}
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={3}>
        {/* Verifiers skeleton */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Skeleton variant="text" width={150} height={20} sx={{ mb: 1 }} />
          <Stack spacing={0.75} sx={{ mb: 2 }}>
            <Skeleton variant="text" width="70%" height={20} />
            <Skeleton variant="text" width="60%" height={20} />
            <Skeleton variant="text" width="80%" height={20} />
          </Stack>
        </Grid>

        {/* Owner/Operators skeleton */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Skeleton variant="text" width={150} height={20} sx={{ mb: 1 }} />
          <Stack spacing={0.75} sx={{ mb: 2 }}>
            <Skeleton variant="text" width="65%" height={20} />
            <Skeleton variant="text" width="75%" height={20} />
            <Skeleton variant="text" width="55%" height={20} />
          </Stack>
        </Grid>
      </Grid>

      {/* State summary skeleton */}
      <Divider sx={{ my: 2 }} />
      <Skeleton variant="text" width={200} height={24} sx={{ mb: 1 }} />
      <Skeleton variant="rectangular" width="100%" height={100} />
    </Paper>
  );
};
